import { VoxelMap, VoxelData } from '@/voxelTypes'
import { buildRectVoxelsWrite } from '@/helpers/voxelBuilders'
import {
  effectiveScribblePlacement,
  effectiveScribbleRadius,
  effectiveScribbleShape,
  needsOrthogonalConnectivity,
  placementPolicy,
  rectanglePlacement,
  sameTypeExpansionLumpiness,
  sameTypeExpansionRadius,
  sameTypeSeedRadius,
} from '@/voxels/registry'
import { expandSpineWithNormals, applySpacingToSpine, canPlaceVoxelOnSurface, placeSurfaceSpineCenters, type ScribbleOpts } from '@/interactions/scribbleHelpers'
import { placeSpineCenters, type Spine, connectSpine } from '@/interactions/spineCore'
import { writeFn } from './layerSystem'
import { expandSameTypeScribble } from '@/interactions/sameTypeBrush'

export type SpineEntry = { key: number; voxel: VoxelData; normal: { x: number; y: number; z: number } }
export type { Spine } from '@/interactions/spineCore'
export function rectangleVoxels(
  min: { x: number; y: number; z: number },
  max: { x: number; y: number; z: number },
  voxel: VoxelData,
  target: VoxelMap,
  mode: 'place' | 'fill' | 'subtract' = 'place',
  bounds?: { x: number; y: number; z: number }
): VoxelMap {
  // rectangleVoxels: write voxels in [min,max] using placement preference
  
  const placementPref = rectanglePlacement(voxel.type || 'filled')
  const policy = placementPolicy(voxel.type)
  const normal = faceNormal((voxel as any).face)

  const write = writeFn(mode, target)
  buildRectVoxelsWrite(min, max, placementPref, (key) => {
    if (mode !== 'subtract' && policy.target === 'surface') {
      if (!bounds || !normal || !canPlaceVoxelOnSurface(voxel.type, key, normal, bounds, target)) return
    }
    write(key, voxel)
  })

  // const count = (max.x - min.x + 1) * (max.y - min.y + 1) * (max.z - min.z + 1)

  return target
}

function faceNormal(face: string | undefined): { x: number; y: number; z: number } | null {
  switch (face) {
    case 'x+': return { x: 1, y: 0, z: 0 }
    case 'x-': return { x: -1, y: 0, z: 0 }
    case 'y+': return { x: 0, y: 1, z: 0 }
    case 'y-': return { x: 0, y: -1, z: 0 }
    case 'z+': return { x: 0, y: 0, z: 1 }
    case 'z-': return { x: 0, y: 0, z: -1 }
    default: return null
  }
}

// Unified scribble expansion for both preview (atoms) and commit (eventRegistry)
export function expandScribble(
  spine: Spine,
  radius: number,
  bounds: { x: number; y: number; z: number },
  target: VoxelMap,
  subtract: boolean,
  opts?: ScribbleOpts & { connect?: boolean; spacingVoxels?: number; connectMinRange?: number; bridgeDiagonals?: boolean; placement?: 'center' | 'onTop' }
): void {
  // Scoped dig (B5): `opts.onlyType` (a canonical type, resolved by the controller from the voxel
  // under the first hit) makes a subtract stroke delete only cells of that material; see ScribbleOpts.
  const firstType = !subtract ? spine[0]?.voxel?.type : undefined
  const sameTypeRadius = firstType ? sameTypeExpansionRadius(firstType) : undefined
  if (!subtract && sameTypeRadius != null) {
    expandSameTypeScribble(spine, bounds, target, {
      radius: sameTypeRadius,
      seedRadius: firstType ? sameTypeSeedRadius(firstType) : undefined,
      lumpiness: firstType ? sameTypeExpansionLumpiness(firstType) : undefined,
    })
    return
  }
  const r = firstType ? effectiveScribbleRadius(firstType, radius) : Math.max(0, radius || 0)
  const effectiveOpts = firstType
    ? {
        ...opts,
        shape: effectiveScribbleShape(firstType, opts?.shape),
        placement: effectiveScribblePlacement(firstType, opts?.placement),
      }
    : opts
  const surfaceOnly = !!firstType && placementPolicy(firstType).target === 'surface'
  // 1) Place centers (uses unmodified target at this point)
  const placed = surfaceOnly
    ? placeSurfaceSpineCenters(spine, bounds, target, !!subtract)
    : placeSpineCenters(spine, bounds, target, !!subtract)
  // 2) Connectivity policy
  const first = placed[0]
  const connectedType = first?.voxel?.type ?? firstType
  const bridgeDiagonals = (effectiveOpts?.bridgeDiagonals != null)
    ? !!effectiveOpts.bridgeDiagonals
    : (connectedType ? needsOrthogonalConnectivity(connectedType) : true)
  const minRange = Math.max(0, Math.floor(effectiveOpts?.connectMinRange ?? 0))
  let connected = effectiveOpts?.connect
    ? connectSpine(placed, bounds, { bridgeDiagonals, minRange })
    : placed
  // 3) Spacing: explicit wins, else derive from radius for spheres/discs (conservative 0.15*r)
  let spacing = Math.max(1, Math.floor(effectiveOpts?.spacingVoxels || 0))
  if ((!spacing || spacing <= 1) && effectiveOpts?.connect) {
    const shape = (effectiveOpts?.shape || 'sphere')
    if (shape === 'sphere' || shape === 'disc') spacing = Math.max(1, Math.floor(r * 0.15))
    else spacing = 1
  }
  connected = spacing > 1 ? applySpacingToSpine(connected, spacing) : connected
  // Finally expand into target (mutable)
  expandSpineWithNormals(connected, r, bounds, target, !!subtract, effectiveOpts)
}
